/**
 * TalentBadge — /professional/badge page.
 * Lets hired talent mint (and copy) their "Verified by Simonara" badge in
 * SVG + PNG formats, with ready-to-paste snippets for LinkedIn, Markdown,
 * and raw HTML.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { BadgeCheck, Copy, Loader2, ShieldCheck, ArrowRight, ExternalLink, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const CopyButton = ({ text, label = "Copy", testId }) => (
    <button
        type="button"
        onClick={() => { navigator.clipboard.writeText(text); toast.success(`${label} copied.`); }}
        className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-background hover:bg-muted px-3 py-1.5 text-xs font-semibold text-foreground transition-colors"
        data-testid={testId}
    >
        <Copy size={12} /> {label}
    </button>
);

export default function TalentBadge() {
    const [state, setState] = useState({ loading: true, badge: null });
    const [minting, setMinting] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/professional/badge");
            setState({ loading: false, badge: data });
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to load badge status.");
            setState({ loading: false, badge: null });
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const mint = async () => {
        setMinting(true);
        try {
            const { data } = await api.post("/professional/badge");
            setState({ loading: false, badge: { ...data, view_count: 0 } });
            toast.success("Badge minted — copy it below.");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not mint badge.");
        } finally {
            setMinting(false);
        }
    };

    const revoke = async () => {
        if (!window.confirm("Revoke your badge? Any live embeds will immediately stop showing it.")) return;
        try {
            await api.delete("/professional/badge");
            toast.success("Badge revoked.");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Revoke failed.");
        }
    };

    const b = state.badge;
    const hasBadge = !!(b && b.token);

    const snippets = useMemo(() => {
        if (!hasBadge) return null;
        const alt = `Verified by Simonara — ${b.display_name || "Trust verified"}`;
        return {
            html: `<a href="${b.profile_url}" target="_blank" rel="noopener noreferrer"><img src="${b.svg_url}" alt="${alt}" width="300" /></a>`,
            markdown: `[![${alt}](${b.svg_url})](${b.profile_url})`,
            linkedin: b.png_url,
            profile: b.profile_url,
        };
    }, [hasBadge, b]);

    return (
        <div className="container-app py-10" data-testid="talent-badge-page">
            <header className="flex items-start justify-between flex-wrap gap-3">
                <div>
                    <p className="overline inline-flex items-center gap-1"><BadgeCheck size={11} /> Verified Badge</p>
                    <h1 className="mt-2 font-heading font-semibold text-3xl tracking-tight text-foreground">
                        Your &ldquo;Verified by Simonara&rdquo; badge
                    </h1>
                    <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
                        Once you&apos;ve been hired through Simonara, you can showcase a verified badge on LinkedIn, your portfolio, or a GitHub README. Employers who click it land on a public verification page — no fake claims possible.
                    </p>
                </div>
                {hasBadge && (
                    <button type="button" onClick={revoke} className="btn-outline text-xs inline-flex items-center gap-2 text-red-600 dark:text-red-400" data-testid="badge-revoke">
                        <Trash2 size={12} /> Revoke badge
                    </button>
                )}
            </header>

            {state.loading ? (
                <div className="mt-8 py-10 flex justify-center" data-testid="badge-loading"><Loader2 className="animate-spin text-emerald-500" /></div>
            ) : !b?.eligible ? (
                <div className="mt-8 card-soft p-8 max-w-xl" data-testid="badge-not-eligible">
                    <div className="flex items-start gap-3">
                        <ShieldCheck size={20} className="text-amber-500 mt-1 shrink-0" />
                        <div>
                            <h2 className="font-heading font-semibold text-xl text-foreground">Not eligible yet.</h2>
                            <p className="mt-2 text-sm text-muted-foreground">
                                Verified badges are reserved for talent who have been hired through Simonara. Once an employer moves you to <b>Hired</b> in their pipeline, come back here to mint your badge.
                            </p>
                            <div className="mt-4 flex flex-wrap gap-3">
                                <Link to="/professional/matches" className="btn-green text-sm inline-flex items-center gap-1" data-testid="badge-cta-matches">
                                    Browse matches <ArrowRight size={12} />
                                </Link>
                                <Link to="/professional/applications" className="btn-outline text-sm" data-testid="badge-cta-applications">
                                    View my applications
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            ) : !hasBadge ? (
                <div className="mt-8 card-soft p-8 max-w-xl" data-testid="badge-mint-panel">
                    <ShieldCheck size={22} className="text-emerald-500" />
                    <h2 className="mt-3 font-heading font-semibold text-xl text-foreground">You&apos;re eligible.</h2>
                    <p className="mt-2 text-sm text-muted-foreground">
                        Mint your badge once. It&apos;s free and revocable at any time. Every view routes through a public verification page hosted by Simonara.
                    </p>
                    <div className="mt-2 text-xs text-muted-foreground">
                        Hired through Simonara: <b className="text-foreground">{b.hired_count} time{b.hired_count === 1 ? "" : "s"}</b>
                    </div>
                    <button
                        type="button"
                        onClick={mint}
                        disabled={minting}
                        className="mt-5 btn-green inline-flex items-center gap-2 disabled:opacity-60"
                        data-testid="badge-mint-btn"
                    >
                        {minting ? <><Loader2 size={14} className="animate-spin" /> Minting…</> : <>Mint my badge <BadgeCheck size={14} /></>}
                    </button>
                </div>
            ) : (
                <div className="mt-8 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-6">
                    {/* LEFT — Preview + snippets */}
                    <div className="space-y-5">
                        <div className="card-soft p-5" data-testid="badge-preview-card">
                            <div className="flex items-center justify-between mb-3">
                                <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Preview</div>
                                <div className="text-[11px] text-muted-foreground">Rendered from the live SVG</div>
                            </div>
                            <div className="rounded-xl bg-muted/40 border border-border p-4 flex justify-center">
                                <img
                                    src={b.svg_url}
                                    alt={`Verified by Simonara — ${b.display_name}`}
                                    className="max-w-full h-auto rounded-lg"
                                    style={{ maxWidth: 480 }}
                                    data-testid="badge-preview-img"
                                />
                            </div>
                            <div className="mt-3 text-[11px] text-muted-foreground">
                                <a href={b.profile_url} target="_blank" rel="noopener noreferrer" className="underline hover:text-foreground inline-flex items-center gap-1" data-testid="badge-public-link">
                                    View public verification page <ExternalLink size={10} />
                                </a>
                            </div>
                        </div>

                        {/* HTML snippet */}
                        <div className="card-soft p-5" data-testid="badge-snippet-html">
                            <div className="flex items-center justify-between mb-3">
                                <div>
                                    <div className="text-sm font-semibold text-foreground">HTML embed</div>
                                    <div className="text-[11px] text-muted-foreground">Paste into personal websites, GitHub Pages, portfolios.</div>
                                </div>
                                <CopyButton text={snippets.html} label="Copy HTML" testId="badge-copy-html" />
                            </div>
                            <pre className="rounded-lg bg-muted/40 border border-border p-3 text-[11px] text-foreground overflow-x-auto whitespace-pre-wrap break-all">{snippets.html}</pre>
                        </div>

                        {/* Markdown snippet */}
                        <div className="card-soft p-5" data-testid="badge-snippet-md">
                            <div className="flex items-center justify-between mb-3">
                                <div>
                                    <div className="text-sm font-semibold text-foreground">Markdown</div>
                                    <div className="text-[11px] text-muted-foreground">For GitHub READMEs, dev.to posts, Notion pages.</div>
                                </div>
                                <CopyButton text={snippets.markdown} label="Copy Markdown" testId="badge-copy-md" />
                            </div>
                            <pre className="rounded-lg bg-muted/40 border border-border p-3 text-[11px] text-foreground overflow-x-auto whitespace-pre-wrap break-all">{snippets.markdown}</pre>
                        </div>

                        {/* LinkedIn PNG */}
                        <div className="card-soft p-5" data-testid="badge-snippet-linkedin">
                            <div className="flex items-center justify-between mb-3">
                                <div>
                                    <div className="text-sm font-semibold text-foreground">LinkedIn (PNG)</div>
                                    <div className="text-[11px] text-muted-foreground">Right-click → Save the PNG, then upload as a Featured item or banner.</div>
                                </div>
                                <div className="flex gap-2">
                                    <CopyButton text={snippets.linkedin} label="Copy PNG URL" testId="badge-copy-png" />
                                    <a
                                        href={snippets.linkedin}
                                        download={`simonara-verified-badge-${b.token}.png`}
                                        className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-500/40 bg-emerald-500/10 hover:bg-emerald-500/20 px-3 py-1.5 text-xs font-semibold text-emerald-700 dark:text-emerald-400 transition-colors"
                                        data-testid="badge-download-png"
                                    >
                                        Download PNG
                                    </a>
                                </div>
                            </div>
                            <div className="rounded-lg bg-muted/40 border border-border p-3 text-[11px] text-muted-foreground break-all">
                                {snippets.linkedin}
                            </div>
                        </div>
                    </div>

                    {/* RIGHT — meta + tips */}
                    <aside className="space-y-4">
                        <div className="card-soft p-5" data-testid="badge-meta">
                            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Your badge</div>
                            <dl className="mt-3 space-y-2 text-sm">
                                <div className="flex justify-between gap-2">
                                    <dt className="text-muted-foreground">Displayed name</dt>
                                    <dd className="text-foreground font-medium truncate">{b.display_name}</dd>
                                </div>
                                <div className="flex justify-between gap-2">
                                    <dt className="text-muted-foreground">Views</dt>
                                    <dd className="text-foreground font-medium" data-testid="badge-view-count">{b.view_count ?? 0}</dd>
                                </div>
                                <div className="flex justify-between gap-2">
                                    <dt className="text-muted-foreground">Minted</dt>
                                    <dd className="text-foreground font-medium">{b.created_at ? new Date(b.created_at).toLocaleDateString() : "—"}</dd>
                                </div>
                                <div className="flex justify-between gap-2">
                                    <dt className="text-muted-foreground">Hired count</dt>
                                    <dd className="text-foreground font-medium">{b.hired_count}</dd>
                                </div>
                            </dl>
                        </div>
                        <div className="card-soft p-5 border-emerald-500/40 bg-emerald-500/5">
                            <div className="text-xs font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-400">Where to use it</div>
                            <ul className="mt-3 text-xs text-muted-foreground space-y-2 leading-relaxed">
                                <li><b className="text-foreground">LinkedIn Featured</b> — upload the PNG as a featured item.</li>
                                <li><b className="text-foreground">Portfolio / personal site</b> — paste the HTML snippet in the header.</li>
                                <li><b className="text-foreground">GitHub README</b> — paste the Markdown snippet at the top.</li>
                                <li><b className="text-foreground">Email signature</b> — link the PNG URL to your Simonara profile.</li>
                            </ul>
                        </div>
                    </aside>
                </div>
            )}
        </div>
    );
}
